import React, {useContext, useState} from "react";
import {Box, Button, CircularProgress, Stack, TextField, Typography} from "@mui/material";
import {HugeiconsIcon} from "@hugeicons/react";
import {ArrowRight01Icon, ArrowLeft01Icon} from "@hugeicons/core-free-icons";
import {toast} from "react-toastify";
import {SignInContext} from "../../../context/SignInContext";
import {c, mono, primaryButtonSx, purpleGradient} from "../../landing/landingTokens";
import {AuthLayout} from "../components/AuthLayout";
import {AuthPasswordField} from "../components/AuthPasswordField";
import {PasswordChecklist, isPasswordValid} from "../components/PasswordChecklist";
import {fieldSx} from "../components/authStyles";

const STEPS = 2;
const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export const RegisterPage = () => {
  const [formData, setFormData] = useState({
    name: "",
    lastname: "",
    email: "",
    password: "",
    confirmPassword: "",
    phone_number: "",
    company_name: "",
  })
  const {onRegister} = useContext(SignInContext);
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [step, setStep] = useState(0);

  const onChange = (e) => {
    const key = e.target.name;
    const value = e.target.value;

    updateForm(key, value);
    setErrors(prev => ({...prev, [key]: ""}));
  }

  const updateForm = (key, value) => {
    setFormData(prev => ({...prev, [key]: value}));
  }

  // Step 1 — who the user is
  const validateAbout = () => {
    const nextErrors = {};

    if (!formData?.name?.trim()) nextErrors.name = "Name is required";
    if (!formData?.lastname?.trim()) nextErrors.lastname = "Last name is required";
    if (!formData?.email?.trim()) {
      nextErrors.email = "Email is required";
    } else if (!EMAIL_PATTERN.test(formData.email.trim())) {
      nextErrors.email = "Please enter a valid email address";
    }
    if (!formData?.phone_number?.trim()) {
      nextErrors.phone_number = "Phone number is required";
    } else if (!/^\d+$/.test(formData.phone_number.trim())) {
      nextErrors.phone_number = "Phone number can contain numbers only";
    }
    return nextErrors;
  }

  // Step 2 — password
  const validateSecurity = () => {
    const nextErrors = {};

    if (!formData?.password) {
      nextErrors.password = "Password is required";
    } else if (!isPasswordValid(formData.password)) {
      nextErrors.password = "Password doesn't meet all the requirements";
    }
    if (!formData?.confirmPassword) {
      nextErrors.confirmPassword = "Please confirm your password";
    } else if (formData.password !== formData.confirmPassword) {
      nextErrors.confirmPassword = "Passwords do not match";
    }
    return nextErrors;
  }

  const registerHandle = async (e) => {
    e.preventDefault();

    if (step === 0) {
      const nextErrors = validateAbout();
      setErrors(nextErrors);
      if (Object.keys(nextErrors).length === 0) setStep(1);
      return;
    }

    const nextErrors = validateSecurity();
    setErrors(nextErrors);

    if (Object.keys(nextErrors).length > 0) {
      return;
    }
    try {
      setLoading(true);
      const {confirmPassword, ...payload} = formData;
      await onRegister({...payload, username: formData?.email});
    } catch (err) {
      toast.error(err.message);
    } finally {
      setLoading(false);
    }
  }

  const goBack = () => {
    setErrors({});
    setStep(0);
  }

  const submitDisabled = loading || (step === 0
    ? !formData?.name || !formData?.lastname || !formData?.email
    : !formData?.password || !formData?.confirmPassword);

  return (
    <AuthLayout
      maxWidth={440}
      title={"Sign up"}
      prompt={"Already have an account?"}
      linkText={"Sign in"}
      linkTo={"/login"}
    >
      {/*REGISTER FORM*/}
      <Stack component={"form"} onSubmit={registerHandle} direction={"column"} gap={0.5} sx={{width: '100%'}}>
        {/*STEP INDICATOR*/}
        <Stack direction={"row"} alignItems={"center"} gap={1.5} sx={{mb: 1.5}}>
          <Stack direction={"row"} gap={0.75} sx={{flex: 1}}>
            {Array.from({length: STEPS}, (_, i) => (
              <Box key={i} sx={{
                flex: 1, height: 4, borderRadius: "980px",
                background: i <= step ? purpleGradient : "rgba(199,210,254,0.55)",
                transition: "background .3s ease",
              }}/>
            ))}
          </Stack>
          <Typography sx={{
            fontFamily: mono, fontSize: 11, letterSpacing: "0.14em",
            textTransform: "uppercase", color: c.inkMuted
          }}>
            Step {step + 1} of {STEPS}
          </Typography>
        </Stack>

        {step === 0 ? (
          <>
            <Stack direction={"row"} gap={1.5}>
              <TextField
                variant={"filled"}
                required
                fullWidth
                label={"Name"}
                value={formData?.name}
                name={"name"}
                onChange={onChange}
                placeholder={"Enter your first name"}
                error={!!errors.name}
                helperText={errors.name || " "}
                sx={fieldSx}
              />
              <TextField
                variant={"filled"}
                required
                fullWidth
                label={"Last name"}
                value={formData?.lastname}
                name={"lastname"}
                onChange={onChange}
                placeholder={"Enter your last name"}
                error={!!errors.lastname}
                helperText={errors.lastname || " "}
                sx={fieldSx}
              />
            </Stack>
            <TextField
              variant={"filled"}
              required
              fullWidth
              label={"Email"}
              type={"email"}
              value={formData?.email}
              name={"email"}
              onChange={onChange}
              placeholder={"Enter your email address"}
              error={!!errors.email}
              helperText={errors.email || " "}
              sx={fieldSx}
            />
            <TextField
              variant={"filled"}
              required
              fullWidth
              label={"Phone"}
              type={"tel"}
              inputProps={{inputMode: "numeric"}}
              value={formData?.phone_number}
              name={"phone_number"}
              onChange={onChange}
              placeholder={"Enter your phone number"}
              error={!!errors.phone_number}
              helperText={errors.phone_number || " "}
              sx={fieldSx}
            />
            <TextField
              variant={"filled"}
              fullWidth
              label={"Company name"}
              value={formData?.company_name}
              name={"company_name"}
              onChange={onChange}
              placeholder={"Enter your company name"}
              helperText={" "}
              sx={fieldSx}
            />
          </>
        ) : (
          <>
            <AuthPasswordField
              autoFocus
              required
              name={"password"}
              label={"Password"}
              value={formData?.password}
              placeholder={"Create a password"}
              onChange={onChange}
              error={!!errors.password}
              helperText={errors.password || " "}
            />
            <PasswordChecklist password={formData?.password} showErrors={!!errors.password}/>
            <AuthPasswordField
              required
              name={"confirmPassword"}
              label={"Confirm password"}
              value={formData?.confirmPassword}
              placeholder={"Re-enter your password"}
              onChange={onChange}
              error={!!errors.confirmPassword}
              helperText={errors.confirmPassword || " "}
              sx={{mt: 1}}
            />
          </>
        )}

        <Button
          fullWidth
          type={"submit"}
          disabled={submitDisabled}
          sx={{...primaryButtonSx, py: 1.4, fontSize: 15, mt: 1}}
          endIcon={loading
            ? <CircularProgress size={16} color={"inherit"}/>
            : <HugeiconsIcon icon={ArrowRight01Icon} size={13}/>}
        >
          {step === 0 ? "Continue" : "Create account"}
        </Button>
        {step === 1 && (
          <Button
            fullWidth
            onClick={goBack}
            disabled={loading}
            startIcon={<HugeiconsIcon icon={ArrowLeft01Icon} size={13}/>}
            sx={{
              textTransform: "none", fontWeight: 500, fontSize: 14, color: c.inkSoft, borderRadius: "980px",
              "&:hover": {bgcolor: "rgba(129,140,248,0.08)", color: c.purpleDeep}
            }}
          >
            Back
          </Button>
        )}
      </Stack>
    </AuthLayout>
  )
}
